import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, inject, signal, computed, OnDestroy, ViewChild } from '@angular/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ClarityModule } from '@clr/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ClrDatagridSortOrder } from '@clr/angular';
import { FormsModule } from '@angular/forms';
import { LOCALE_ID } from '@angular/core';
import { TranscriptionEditorComponent } from '../../../transcription/components/transcription-editor/transcription-editor.component';
import { DurationFormatPipe } from '../../../../Pipes/duration-format.pipe';
import { BroadcastsService } from '../../../programplan/services/broadcasts.service';
import { Broadcast } from '../../../../models/broadcast';
import { UploadDropzoneComponent, UploadCompleteEvent } from '../../../../shared/components/upload-dropzone/upload-dropzone.component';
import { OverlayPanelComponent } from '../../../../shared/components/overlay-panel/overlay-panel.component';
import { AuthorizationService } from '../../../../../security/authorization.service';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { downloadCsv } from '../../../../shared/utils/csv-export';

@Component({
  selector: 'app-archive',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, TranscriptionEditorComponent, DurationFormatPipe, UploadDropzoneComponent, OverlayPanelComponent, HasPermissionDirective],
  providers: [{ provide: LOCALE_ID, useValue: 'de-DE' }],
  templateUrl: './archive.component.html',
  styleUrl: './archive.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ArchiveComponent implements OnInit, OnDestroy {
  private broadcastsService = inject(BroadcastsService);
  private readonly authz = inject(AuthorizationService);

  // RBAC: Upload nur für Admin/Redakteur (Dropzone bleibt sichtbar, gesperrt)
  get canManageBroadcasts(): boolean { return this.authz.hasPermission('manage-broadcasts'); }

  // Reference to upload dropzone for drag events
  @ViewChild(UploadDropzoneComponent) uploadDropzone!: UploadDropzoneComponent;

  // Reaktive Liste von Broadcasts
  private readonly broadcasts = signal<Broadcast[]>([]);
  private broadcastEventSub?: Subscription;

  // Computed Signal für das Grid
  readonly broadcastList = computed(() => this.broadcasts());

  selectedDate: Date = new Date();
  sortOrder = ClrDatagridSortOrder.ASC;
  editorVisible = false;
  selectedBroadcast: any = null;
  isLoading: boolean = true;

  // Delete confirmation dialog state
  deleteDialogVisible = false;
  broadcastToDelete: Broadcast | null = null;
  isDeleting = false;

  // Pagination and search state
  searchTerm = '';
  pageSizeOptions = [5, 10, 50, 100, 200, 500, 1000];
  pageSize = 50;
  currentPage = 1;
  totalItems = 0;
  nextToken: string | null = null;
  private tokenHistory: (string | null)[] = [null]; // Track tokens for each page
  private searchSubject = new Subject<string>();


  ngOnInit(): void {
    // Initiale Ladung starten
    const today = new Date();
    this.selectedDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    // Debounced search
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.currentPage = 1;
      this.nextToken = null;
      this.tokenHistory = [null];
      this.loadData();
    });

    this.loadData();

    // Keep subscription for real-time updates
    this.broadcastsService.subscribeToBroadcasts().subscribe({
      next: (result) => {
        const data = result.data as { onBroadcastsChanged: Broadcast };
        const updatedBroadcast = data.onBroadcastsChanged;

        const found = this.broadcasts().find(b => b.key === updatedBroadcast.key);
        if (found) {
          console.log('Aktualisierter Broadcast:', updatedBroadcast, found);
          const updatedBroadcasts = this.broadcasts().map(b =>
            b.key === updatedBroadcast.key ? updatedBroadcast : b
          );
          this.broadcasts.set(updatedBroadcasts);
        } else if (updatedBroadcast.key.startsWith('ARCHIV/')) {
          // Only add new archive broadcasts on first page with no search filter
          if (this.currentPage === 1 && !this.searchTerm) {
            console.log('Neuer Broadcast:', updatedBroadcast);
            this.broadcasts.set([updatedBroadcast, ...this.broadcasts()]);
            this.totalItems++;
          }
        }
      },
      error: (error) => {
        console.error('Fehler beim Abonnieren der Daten:', error);
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    const tokenToUse = this.currentPage > 1 ? this.tokenHistory[this.currentPage - 1] : null;

    this.broadcastsService.getArchiveBroadcasts(
      this.pageSize,
      tokenToUse,
      this.searchTerm
    ).subscribe({
      next: (result) => {
        const data = result.data.archiveBroadcasts;
        this.broadcasts.set(data.items);
        this.totalItems = data.totalCount;
        this.nextToken = data.nextToken;

        // Store the nextToken for the next page
        if (this.currentPage >= this.tokenHistory.length) {
          this.tokenHistory.push(data.nextToken);
        } else {
          this.tokenHistory[this.currentPage] = data.nextToken;
        }

        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading archive:', error);
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  onPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadData();
    }
  }

  onNextPage(): void {
    if (this.nextToken) {
      this.currentPage++;
      this.loadData();
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = Number(newSize);
    this.currentPage = 1;
    this.nextToken = null;
    this.tokenHistory = [null];
    this.loadData();
  }

  ngOnDestroy(): void {
    this.broadcastEventSub?.unsubscribe();
  }


  parseGermanDateString(dateString: string): Date | null {
    const [day, month, year] = dateString.split('.');
    if (!day || !month || !year) return null;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  
  onDateChange(value: string | Date) {
    let parsedDate: Date;

    if (typeof value === 'string') {
      const [day, month, year] = value.split('.');
      if (day && month && year) {
        parsedDate = new Date(Date.UTC(+year, +month - 1, +day));
      } else {
        parsedDate = new Date();
      }
    } else {
      // Wenn es ein Date-Objekt ist, in UTC-Midnight konvertieren
      parsedDate = new Date(Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      ));
    }
  
    this.selectedDate = parsedDate;
  
    this.broadcasts.set([]);
    this.broadcastsService.getData('ARCHIV', this.selectedDate).subscribe({
      next: (result) => {
        console.log('Daten geladen:', result.data.broadcasts);
        this.broadcasts.set(result.data.broadcasts);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Fehler beim Laden der Daten:', error);
      }
    });
  }

  getStatusDescription(broadcast: Broadcast): string {
    let type = 'Video on Demand';
    let result = type;
    if (broadcast.createdAt) {
      result = type + ' (Transkription läuft...)';
    }
    if (broadcast.wavTranscriptCreatedAt) {
      result = type + ' (Transkription abgeschlossen)';
    }
    return result;
  }

  editBroadcast(broadcast: Broadcast): void {
    this.selectedBroadcast = broadcast;
    this.editorVisible = true;
  }

  closeEditor() {
    this.editorVisible = false;
    this.selectedBroadcast = null;
  }

  exportToCsv() {
    const headers = [
      'Status',
      'Titel der Sendung',
      'EpisodeID',
      'Bearbeiter',
      'Dauer',
      'Erstellt',
      'Abschluss'
    ];

    const rows = this.broadcastList().map(broadcast => {
      let wav_duration_seconds = '';
      const totalSeconds = Math.floor(Number(broadcast.bruttoDuration));
      if (!isNaN(totalSeconds) && totalSeconds) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        wav_duration_seconds = `${pad(minutes)}:${pad(seconds)}`;
      }
      const csvDate = (value: string) => value ? formatDate(value, 'dd.MM.yyyy HH:mm', 'de-DE') : '';
      return [
        "vod",
        broadcast.title ?? '',
        broadcast.episodeId ?? '',
        broadcast.editors?.join(', ') ?? '',
        wav_duration_seconds,
        csvDate(broadcast.createdAt),
        csvDate(broadcast.wavTranscriptCreatedAt)
      ];
    });

    downloadCsv(`Transkriptionen_${this.selectedDate.toISOString().split('T')[0]}.csv`, headers, rows);
  }

  // ===== Upload Event Handlers =====

  onUploadComplete(event: UploadCompleteEvent): void {
    console.log('Upload complete:', event);
    // The broadcast will be created automatically by the backend FileDispatcherService
    // and will appear via the GraphQL subscription
  }

  onUploadError(error: string): void {
    console.error('Upload error:', error);
  }

  // Proxy drag events to the upload dropzone component
  onDragOver(event: DragEvent): void {
    this.uploadDropzone?.onDragOver(event);
  }

  onDragLeave(event: DragEvent): void {
    this.uploadDropzone?.onDragLeave(event);
  }

  onDrop(event: DragEvent): void {
    this.uploadDropzone?.onDrop(event);
  }

  // ===== Delete Event Handlers =====

  confirmDelete(broadcast: Broadcast): void {
    this.broadcastToDelete = broadcast;
    this.deleteDialogVisible = true;
  }

  cancelDelete(): void {
    this.deleteDialogVisible = false;
    this.broadcastToDelete = null;
  }

  executeDelete(): void {
    if (!this.broadcastToDelete) return;

    this.isDeleting = true;
    const key = this.broadcastToDelete.key;

    this.broadcastsService.deleteArchiveBroadcast(key).subscribe({
      next: (result) => {
        console.log('Broadcast deleted:', result);
        // Remove from local list
        this.broadcasts.set(this.broadcasts().filter(b => b.key !== key));
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.broadcastToDelete = null;
      },
      error: (error) => {
        console.error('Error deleting broadcast:', error);
        this.isDeleting = false;
      }
    });
  }
}
